import type React from 'react';
import type { UserRole } from '@/api/models';
import {
    canWriteAccounts,
    deriveAccountsAccess,
} from '@/pages/accounting/books/chartOfAccounts/accountsAccess';
import {
    canReadDividends,
    deriveDividendsAccess,
} from '@/pages/accounting/registers/dividends/dividendsAccess';
import {
    canReadSalaries,
    deriveSalariesAccess,
} from '@/pages/accounting/registers/salaries/salariesAccess';
import { getAuthMethod, getUser } from '@/utils/authSession';
import { PATH } from '@/router/PATH';
import {
    AddAccountSurface,
    AddAssetSurface,
    AddAttributeTypeSurface,
    AddBranchSurface,
    AddBrandSurface,
    AddCategorySurface,
    AddEmployeeSurface,
    AddShareholderSurface,
    AddSupplierSurface,
    AddUserSurface,
} from './components/surfaces';

/**
 * Which action opens which real UI.
 *
 * Every one of the catalog's twenty-six rows has a home somewhere else in the
 * product, and the launcher's promise is that what is doable in its home place
 * is doable from here. The only way to keep that promise without growing a
 * second copy of every form is to **mount the feature's own component** — or,
 * when acting on it needs the whole page rather than a drawer, navigate there
 * instead (see `ACTION_DESTINATIONS` below).
 *
 * So an action id maps to a *surface*: the component that is its real UI, plus
 * the gate that decides whether this session may open it.
 *
 * **There is no fall-through any more.** Until BOS-181 an unregistered id
 * opened a placeholder modal saying its form was not built, which was honest
 * while most of the catalog was a fixture and became the reason two thirds of
 * the page did nothing. The catalog and these two maps are now kept in step by
 * a test: an action with neither a surface nor a destination is a build
 * failure, not a shrug.
 */

/**
 * Who may run an action.
 *
 * A gate is named after the **feature's** rule, not after a role, because the
 * rules are not all role-shaped: payroll needs an OrgAdmin *on a password
 * session*, and a PIN session of the same admin is refused. Resolving it here
 * means there is still exactly one definition of that rule — the register's
 * own — and the catalog just points at it.
 */
export type ActionGate =
    | 'none'
    | 'salariesFull'
    | 'management'
    | 'accountsWrite'
    | 'dividendsFull'
    | 'accountingRead';

/**
 * Who counts as management for `'management'`.
 *
 * The same pair the Team sidebar entry carries, because this gate exists to
 * mirror it: an action openable from the launcher that its own page would not
 * show you is a hole, not a shortcut.
 */
const MANAGEMENT_ROLES: UserRole[] = ['OrgAdmin', 'BranchAdmin'];

/**
 * Who counts as an accounting reader for `'accountingRead'`.
 *
 * Deliberately the same pair as `MANAGEMENT_ROLES` with no password
 * requirement — it mirrors the sidebar's own Accounting nav gate exactly
 * (`roles: ['OrgAdmin', 'BranchAdmin']`, any session type), not the stricter
 * `'management'` gate. A BranchAdmin on a kiosk/PIN token can already see
 * every accounting nav item from the sidebar; refusing the same navigation
 * from the launcher would be a launcher-only restriction with no page behind
 * it to justify it.
 */
const ACCOUNTING_READ_ROLES: UserRole[] = ['OrgAdmin', 'BranchAdmin'];

/**
 * Why an action cannot be run right now, or `null` when it can.
 *
 * The two refusals read differently on purpose, exactly as the salaries
 * register's own notice distinguishes them: a kiosk session has a remedy it
 * owns — sign in with a password — and a BranchAdmin does not, and is better
 * told plainly than invited to retry.
 */
export type ActionRefusal = 'kiosk' | 'forbidden';

/**
 * Title and body per **gate** and refusal, keyed rather than branched at the
 * call site — the shape `SALARIES_ACCESS_COPY` already uses, and the one that
 * makes adding a gate or a refusal a compile error rather than a missing case.
 *
 * Keyed by gate as well as by kind because the audiences differ and saying so
 * vaguely would be worse than not saying it. Payroll is organisation
 * administrators only, so a BranchAdmin refused from it needs to hear exactly
 * that; the management surfaces admit BranchAdmins, so telling one of them that
 * the action is "limited to organisation administrators" would be false, and
 * false in the direction that makes someone stop trying something they are
 * allowed to do.
 */
export const REFUSAL_COPY: Record<
    Exclude<ActionGate, 'none'>,
    Record<ActionRefusal, { titleKey: string; bodyKey: string }>
> = {
    salariesFull: {
        kiosk: {
            titleKey: 'controlCenter.refusal.salariesFull.kiosk.title',
            bodyKey: 'controlCenter.refusal.salariesFull.kiosk.body',
        },
        forbidden: {
            titleKey: 'controlCenter.refusal.salariesFull.forbidden.title',
            bodyKey: 'controlCenter.refusal.salariesFull.forbidden.body',
        },
    },
    management: {
        kiosk: {
            titleKey: 'controlCenter.refusal.management.kiosk.title',
            bodyKey: 'controlCenter.refusal.management.kiosk.body',
        },
        forbidden: {
            titleKey: 'controlCenter.refusal.management.forbidden.title',
            bodyKey: 'controlCenter.refusal.management.forbidden.body',
        },
    },
    accountsWrite: {
        kiosk: {
            titleKey: 'controlCenter.refusal.accountsWrite.kiosk.title',
            bodyKey: 'controlCenter.refusal.accountsWrite.kiosk.body',
        },
        forbidden: {
            titleKey: 'controlCenter.refusal.accountsWrite.forbidden.title',
            bodyKey: 'controlCenter.refusal.accountsWrite.forbidden.body',
        },
    },
    dividendsFull: {
        kiosk: {
            titleKey: 'controlCenter.refusal.dividendsFull.kiosk.title',
            bodyKey: 'controlCenter.refusal.dividendsFull.kiosk.body',
        },
        forbidden: {
            titleKey: 'controlCenter.refusal.dividendsFull.forbidden.title',
            bodyKey: 'controlCenter.refusal.dividendsFull.forbidden.body',
        },
    },
    accountingRead: {
        kiosk: {
            titleKey: 'controlCenter.refusal.accountingRead.kiosk.title',
            bodyKey: 'controlCenter.refusal.accountingRead.kiosk.body',
        },
        forbidden: {
            titleKey: 'controlCenter.refusal.accountingRead.forbidden.title',
            bodyKey: 'controlCenter.refusal.accountingRead.forbidden.body',
        },
    },
};

/** What a completed action leaves behind for the page to confirm. */
export type ActionOutcome = {
    /** Resolved by the page, so the surface never needs `t`. */
    messageKey: string;
    messageParams?: Record<string, string>;
    /** Where the record landed, and what to call the link to it. */
    to: string;
    linkKey: string;
};

/**
 * The contract every surface adapter implements.
 *
 * Deliberately narrow and feature-agnostic: the Control Center's View renders
 * a `Surface` without importing anything from the feature behind it. Everything
 * feature-shaped — which component, which props, how a save becomes a sentence
 * — lives in the adapter under `components/surfaces/`.
 *
 * There is no `isOpen`: a surface is **mounted only while it is open**. That is
 * not a style preference. The employee drawer fires an unfiltered register read
 * on mount for its duplicate-id check, so a surface left mounted closed is a
 * request on every visit to the home page.
 */
export type ActionSurfaceProps = {
    onClose: () => void;
    /** Fired on a real success only — never on cancel. */
    onDone: (outcome: ActionOutcome) => void;
};

export type ActionSurface = {
    gate: ActionGate;
    Surface: React.ComponentType<ActionSurfaceProps>;
};

export const ACTION_SURFACES: Record<string, ActionSurface | undefined> = {
    // Five catalogue forms whose own pages are open to any signed-in caller.
    // Gating them here would make the launcher stricter than the page it
    // mirrors, which is the opposite of the promise it makes.
    addAttributeType: { gate: 'none', Surface: AddAttributeTypeSurface },
    addBranch: { gate: 'none', Surface: AddBranchSurface },
    addBrand: { gate: 'none', Surface: AddBrandSurface },
    addCategory: { gate: 'none', Surface: AddCategorySurface },
    addSupplier: { gate: 'none', Surface: AddSupplierSurface },

    addEmployee: { gate: 'salariesFull', Surface: AddEmployeeSurface },

    // Team is a management surface — sellers never see it — and the user form
    // asks a Seller to pick from an empty list of assignable roles. Refusing
    // before the form is mounted is the difference between an explanation and
    // a form that cannot be completed.
    addUser: { gate: 'management', Surface: AddUserSurface },

    // Three accounting drawers, each behind the write-gate its own page
    // enforces (see `accountsAccess.ts` / `dividendsAccess.ts`) — stricter
    // than `'accountingRead'`, the same way `addEmployee`'s `'salariesFull'`
    // sits inside `peopleAndSystem` next to `addUser`'s looser `'management'`.
    addAccount: { gate: 'accountsWrite', Surface: AddAccountSurface },
    addAsset: { gate: 'none', Surface: AddAssetSurface },
    addShareholder: { gate: 'dividendsFull', Surface: AddShareholderSurface },
};

export const surfaceFor = (actionId: string): ActionSurface | null =>
    ACTION_SURFACES[actionId] ?? null;

/**
 * A navigate-away action: no modal, no surface — pressing it routes straight
 * to its own screen. `gate` is checked the same way a surface's is, through
 * the same `refusalFor`/`actionRefusal` path, so there is exactly one gating
 * mechanism for both kinds of action.
 */
export type ActionDestination = { gate: ActionGate; to: string };

export const ACTION_DESTINATIONS: Record<
    string,
    ActionDestination | undefined
> = {
    accountingJournal: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.BOOKS.JOURNAL,
    },
    accountingTrialBalance: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.BOOKS.TRIAL_BALANCE,
    },
    accountingVat: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.BOOKS.VAT,
    },
    recordOperation: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.OPERATIONS.RECORD,
    },

    // The four outside Accounting. Each lands on the screen that *is* the
    // action — `/products/new` is the product form, not the product list —
    // which is the line drawn in BOS-181: a row whose only destination is the
    // page that happens to host it somewhere was removed instead.
    addProduct: { gate: 'none', to: PATH.PRODUCTS.NEW },
    productList: { gate: 'none', to: PATH.PRODUCTS.ROOT },
    newPurchase: { gate: 'none', to: PATH.PURCHASES.MANUAL_IMPORT },
    supplierList: { gate: 'none', to: PATH.SUPPLIERS },
    // Analytics is a management surface in the sidebar; the launcher matches.
    financialPosition: {
        gate: 'management',
        to: PATH.ANALYTICS.FINANCIAL_POSITION,
    },
    operationsActivity: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.OPERATIONS.ACTIVITY,
    },
    operationTemplates: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.OPERATIONS.TEMPLATES,
    },
    operationCategories: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.OPERATIONS.CATEGORIES,
    },
    balanceSheet: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.STATEMENTS.BALANCE_SHEET,
    },
    profitLoss: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.STATEMENTS.PROFIT_LOSS,
    },
    accountingPeriods: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.CONFIGURATIONS.PERIODS,
    },
    accountingSettings: {
        gate: 'accountingRead',
        to: PATH.ACCOUNTING.CONFIGURATIONS.SETTINGS,
    },
};

export const destinationFor = (actionId: string): ActionDestination | null =>
    ACTION_DESTINATIONS[actionId] ?? null;

/**
 * Whether this session is refused, and why.
 *
 * Read at the moment of the press rather than memoised for the page's life:
 * the answer is a function of the stored session, and reading it late costs
 * nothing next to being wrong after a re-auth.
 */
export const refusalFor = (gate: ActionGate): ActionRefusal | null => {
    if (gate === 'none') {
        return null;
    }

    if (gate === 'management') {
        // Role is only half of it, the same way `branchesTabsMeta` and
        // `salariesAccess` say it is: the API's admin policies also require
        // `auth_method=password`, so an OrgAdmin holding a PIN-issued kiosk
        // token 403s exactly as a Seller does. Failing closed on an unknown
        // auth method is deliberate.
        if (getAuthMethod() !== 'password') {
            return 'kiosk';
        }
        const user = getUser();
        return user && MANAGEMENT_ROLES.includes(user.role)
            ? null
            : 'forbidden';
    }

    if (gate === 'accountingRead') {
        const user = getUser();
        return user && ACCOUNTING_READ_ROLES.includes(user.role)
            ? null
            : 'forbidden';
    }

    if (gate === 'accountsWrite') {
        const access = deriveAccountsAccess(getUser(), getAuthMethod());
        if (canWriteAccounts(access)) {
            return null;
        }
        return access.kind === 'kiosk' ? 'kiosk' : 'forbidden';
    }

    if (gate === 'dividendsFull') {
        const access = deriveDividendsAccess(getUser(), getAuthMethod());
        if (canReadDividends(access)) {
            return null;
        }
        return access.kind === 'kiosk' ? 'kiosk' : 'forbidden';
    }

    const access = deriveSalariesAccess(getUser(), getAuthMethod());
    if (canReadSalaries(access)) {
        return null;
    }
    return access.kind === 'kiosk' ? 'kiosk' : 'forbidden';
};

/** The gate behind an action, whether it opens a surface or navigates. */
const gateFor = (actionId: string): ActionGate | null =>
    surfaceFor(actionId)?.gate ?? destinationFor(actionId)?.gate ?? null;

/** The refusal for an action, or `null` — for an unregistered one, always null. */
export const actionRefusal = (actionId: string): ActionRefusal | null => {
    const gate = gateFor(actionId);
    return gate ? refusalFor(gate) : null;
};

/**
 * The copy for a refusal the popover is about to show.
 *
 * Resolved from the action rather than passed alongside the refusal, so no
 * caller has to carry a gate it has no other use for. The fallback is
 * unreachable — a `'none'` gate and an unregistered action both refuse nobody,
 * so there is no refusal to render copy for — and exists to keep the lookup
 * total rather than to describe a case.
 */
export const refusalCopyFor = (
    actionId: string,
    refusal: ActionRefusal,
): { titleKey: string; bodyKey: string } => {
    const gate = gateFor(actionId);
    return REFUSAL_COPY[gate && gate !== 'none' ? gate : 'salariesFull'][
        refusal
    ];
};
